import moment from "moment";
import { legacyTextFromSegments, normalizeLayoutSegments } from "helper/briqueLayoutSegments";
import { resolveGroupForDeliveryDate } from "helper/packageRotationHelper";

/** Texte commentaire API / panier à partir de layout_segments + fallback legacy. */
const briqueItemTextForApi = (item) => {
  const joined = legacyTextFromSegments(normalizeLayoutSegments(item));
  return joined || item.text || null;
};

export const orderStateToTotal = (order) => {
  let itemsCost = 0;
  const participantsNumber = order?.deliveryInfo?.person || 1;
  const useClassicPricing = order?.priceInfo?.useClassicPricing !== undefined ? order?.priceInfo?.useClassicPricing : null;

  // Si cocktail et pas en mode classique, les forfaits sont exclus du prix classique, ce sont les prix dans priceInfo qui compte
  if (order.prestationType === 'caterer' && useClassicPricing !== true &&
    (order?.priceInfo?.catererPackagePiecesPriceHtPerParticipant || order?.priceInfo?.catererPackageSoftsPriceHtPerParticipant || order?.priceInfo?.catererPackageAlcoholsPriceHtPerParticipant)) {
    itemsCost = (order.priceInfo.catererPackagePiecesPriceHtPerParticipant + order.priceInfo.catererPackageSoftsPriceHtPerParticipant + order.priceInfo.catererPackageAlcoholsPriceHtPerParticipant) * participantsNumber;
  }
  
  // ajoute le prix des items sauf si prestationType = caterer et item.type = productPackage et pas en mode classique
  itemsCost += order.items ? order.items.filter((item) => {
    if (order.prestationType === 'caterer') {
      if (useClassicPricing !== true && item.type === 'productPackage') {
        return false;
      }
    }
    return true;
  }).reduce(
    (total, item) => total + ((item.price || 0) * item.quantity),
    0
  ) : 0;

  const deliveryCost = order.deliveryCost;
  // Plusieurs réductions possibles (typées Commercial/SAV). Rétro-compat : un
  // ancien `order.reduction` unique est traité comme une liste à un élément.
  const reductions = Array.isArray(order.reductions)
    ? order.reductions
    : (order.reduction && (order.reduction.direct || order.reduction.proportional) ? [order.reduction] : []);
  let reductionAmount = 0;
  for (const r of reductions) {
    // Le back applique le pourcentage quel que soit le mode de tarification
    // (Api\Pro\OrderController : ht = totalHtSansPromo * percent / 100). La
    // condition `useClassicPricing !== true` faisait diverger le total affiché
    // du total enregistré sur toutes les prestations en mode classique.
    if (r.proportional) {
      reductionAmount += ((itemsCost + deliveryCost) * Number(r.proportional)) / 100;
    } else if (r.direct) {
      reductionAmount += Number(r.direct) || 0;
    }
  }

  const totalHT = itemsCost - reductionAmount + deliveryCost;
  const totalHTWithoutReduction = itemsCost + deliveryCost;

  return {
    totalHT,
    totalHTWithoutReduction
  };
};

/**
 * Convertit les items des briques B2B (éditeur devis) en lignes `order.items`
 * (même forme que les ajouts classiques forfaits / groupes / produits).
 * @param {Array} briqueDevisBlocks — `order.briqueDevis`
 * @param {object} catalog — { products, productGroups, productPackages, tenderCatalog }
 * @param {object} order — prestation courante (personnes, type…)
 */
export const briqueDevisToOrderItems = (briqueDevisBlocks, catalog, order) => {
  if (!briqueDevisBlocks || !briqueDevisBlocks.length) {
    return [];
  }
  const products = catalog?.products || [];
  const productGroups = catalog?.productGroups || [];
  const productPackages = catalog?.productPackages || [];
  const tenderCatalog = catalog?.tenderCatalog || null;
  const prestationType = order?.prestationType;
  const person = Number(order?.deliveryInfo?.person) || 1;

  const out = [];

  for (const block of briqueDevisBlocks) {
    const items = block?.items || [];
    for (const item of items) {
      if (!item || !item.type) {
        continue;
      }

      if (item.type === "product") {
        const p = products.find((x) => x.id === item.product_id);
        if (!p) {
          continue;
        }
        out.push({
          type: "product",
          name: item.name_override || item.effective_name || p.name,
          quantity: item.quantity != null ? Number(item.quantity) : 1,
          price: item.unit_price != null ? item.unit_price : p.price,
          product_id: item.product_id,
          classification_name: p.classificationName,
          renammed: item.renammed != null && item.renammed !== "" ? item.renammed : null,
          text: briqueItemTextForApi(item),
        });
      } else if (item.type === "group") {
        const g = productGroups.find((x) => x.id === item.product_group_id);
        if (!g) {
          continue;
        }
        // Groupes à slots : le contenu concret dépend de la date de livraison
        const resolvedGroup = resolveGroupForDeliveryDate(
          g,
          order?.deliveryInfo?.deliveryDate,
          catalog?.rotationSlots
        );
        out.push({
          type: "productGroup",
          name: item.name_override || item.effective_name || g.name,
          price: item.unit_price != null ? item.unit_price : g.price,
          quantity: item.quantity != null ? Number(item.quantity) : 1,
          group_id: g.id,
          classification_name: g.classification_name || g.classificationName || null,
          products: (resolvedGroup.products || []).map((product) => ({
            quantity: product.quantity,
            product_id: product.product_id,
          })),
          renammed: item.renammed != null && item.renammed !== "" ? item.renammed : null,
          text: briqueItemTextForApi(item),
        });
      } else if (item.type === "forfait") {
        const pkg = productPackages.find((x) => x.id === item.package_id);
        if (!pkg) {
          continue;
        }
        const packageParticipantsNumber = pkg.participants_number || 1;
        const quantity = Number(item.nb_persons) || person;
        const tenderCatalogItem =
          tenderCatalog?.items &&
          tenderCatalog.items.find((i) => i.package_id === pkg.id);
        const renammed = tenderCatalogItem ? tenderCatalogItem?.renammed : null;

        let tenderCatalogItemPricing = tenderCatalogItem?.pricings?.find(
          (pricing) =>
            pricing.min_persons <= person &&
            pricing.max_persons >= person &&
            pricing.price !== null &&
            pricing.prestation_types &&
            pricing.prestation_types.includes(prestationType)
        );

        if (!tenderCatalogItemPricing) {
          const pricingsWithPrice = tenderCatalogItem?.pricings?.filter(
            (pricing) =>
              pricing.price !== null &&
              pricing.prestation_types &&
              pricing.prestation_types.includes(prestationType) &&
              pricing.min_persons <= person
          );
          const closestPricing = pricingsWithPrice?.reduce((closest, pricing) => {
            const currentDistance = Math.abs(pricing.max_persons - person);
            const closestDistance = Math.abs(closest.max_persons - person);
            return currentDistance < closestDistance ? pricing : closest;
          }, pricingsWithPrice[0]);
          if (closestPricing) {
            tenderCatalogItemPricing = closestPricing;
          }
        }

        let catalogUnitPrice = tenderCatalogItemPricing
          ? tenderCatalogItemPricing.price
          : tenderCatalogItem?.price != null
            ? tenderCatalogItem.price
            : pkg.price;
        if (catalogUnitPrice == null || catalogUnitPrice === undefined) {
          catalogUnitPrice = pkg.price ?? 0;
        }
        const price =
          item.price_per_person != null
            ? item.price_per_person
            : Math.round(Number(catalogUnitPrice) / packageParticipantsNumber);

        const hasStoredComposition =
          Array.isArray(item.products) || Array.isArray(item.product_parts);

        let productsLines;
        let productPartsLines;

        if (hasStoredComposition) {
          productsLines = (item.products || []).map((row) => ({
            quantity: Number(row.quantity) || 0,
            product_id: row.product_id,
            sweet: row.sweet,
          }));
          productPartsLines = (item.product_parts || []).map((row) => ({
            quantity: Number(row.quantity) || 0,
            product_part_id: row.product_part_id,
            sweet: row.sweet,
          }));
        } else {
          productsLines = (pkg.products || []).map((product) => ({
            quantity: Math.ceil(
              product.quantity * (quantity / packageParticipantsNumber)
            ),
            product_id: product.product_id,
            sweet: product.sweet,
          }));
          productPartsLines = (pkg.product_parts || []).map((productPart) => ({
            quantity: Math.ceil(
              productPart.quantity * (quantity / packageParticipantsNumber)
            ),
            product_part_id: productPart.product_part_id,
            sweet: productPart.sweet,
          }));
        }

        const extras = item.extra_articles || [];
        for (const a of extras) {
          const q = Number(a.qty) || 0;
          if (!q) continue;
          if (a.source_type === "product" && a.source_id) {
            productsLines.push({
              quantity: q,
              product_id: a.source_id,
              sweet: !!a.sweet,
            });
          } else if (a.source_type === "part" && a.source_id) {
            productPartsLines.push({
              quantity: q,
              product_part_id: a.source_id,
              sweet: !!a.sweet,
            });
          }
        }

        out.push({
          name: item.name_override || item.effective_name || pkg.name,
          price,
          quantity,
          type: "productPackage",
          package_id: pkg.id,
          classification_name: pkg.classification_name || pkg.classificationName || null,
          text: briqueItemTextForApi(item),
          products: productsLines,
          product_parts: productPartsLines,
          renammed: item.renammed != null ? item.renammed : renammed,
          caterer_price_group: item.caterer_price_group ?? pkg.caterer_price_group,
        });
      }
    }
  }
  return out;
};

/**
 * Prend les commandes dans le state et formate pour l'api
 * @param {object} orderInfo — state orderEdition
 * @param {object|null} catalog — optionnel : offre + AO pour convertir les briques B2B en articles
 */
export const multiOrdersStoreToApi = (orderInfo, catalog = null) => {
  const parseIntegerOrNull = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };


  return orderInfo.orders.map((rawOrder, orderIndex) => {
    // Mode briques : les frais de livraison ne viennent que d'une brique « livraison ».
    // Sans elle, le champ n'est pas affiché et une valeur restée d'avant était comptée
    // dans le total sans être facturée (F/B/26/09/07644, Trello SSPNmz59) : on envoie 0,
    // pour toutes les prestations du devis, pas seulement celle qui est affichée.
    const hasBriqueLivraison = (rawOrder.briqueDevis || []).some(
      (b) => (b.name || "").toLowerCase().includes("livraison")
    );
    const order = rawOrder.briqMode === true && !hasBriqueLivraison
      ? { ...rawOrder, deliveryCost: 0 }
      : rawOrder;
    const deliveryDate = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.deliveryDate
      ? moment(orderInfo.orders[orderIndex].deliveryInfo.deliveryDate, "YYYY/MM/DD").format("YYYY-MM-DD")
      : null;

    const deliveryStartAt = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.startAt || null;
    const deliveryEndAt = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.endAt || null;
    const deliveryArrivedAtStart = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.arrivedAtStart || null;
    const deliveryArrivedAtEnd = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.arrivedAtEnd || null;
    const recoveryDate = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.recoveryDate
      ? moment(orderInfo.orders[orderIndex].deliveryInfo.recoveryDate, "YYYY/MM/DD").format("YYYY-MM-DD")
      : null;
    const recoveryStartAt = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.recoveryStartAt || null;
    const recoveryEndAt = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.recoveryEndAt || null;
    const isCaterer = order.prestationType === 'caterer';
    const useClassicPricing = orderInfo?.orders?.[orderIndex]?.priceInfo?.useClassicPricing;
    // En mode brique, chaque forfait est facturé à son propre prix (price_per_person) :
    // on NE passe PAS par le modèle caterer "par participant" (pieces / softs / alcohol).
    const briqueMode = order.briqMode === true;
    const serviceInfoState = order?.serviceInfo || {};
    const briqueItems =
      catalog && order.briqueDevis?.length
        ? briqueDevisToOrderItems(order.briqueDevis, catalog, order)
        : [];
    // En mode brique, les articles sont entièrement gérés via les briques.
    // On n'inclut PAS order.items (qui contient les articles rechargés depuis l'API)
    // pour éviter les doublons lors d'un 2ème save.
    const mergedItems = order.briqMode
      ? briqueItems
      : [...(order.items || []), ...briqueItems];

    const { totalHT, totalHTWithoutReduction } = orderStateToTotal({
      ...order,
      items: mergedItems,
    });

    const articles = mergedItems.map((item) => {
      if (item.type === 'productGroup') {
        const groupArticle = {
          name: item.name,
          renamedName: item?.renammed || null,
          group: item.group_id,
          quantity: item.quantity,
          price: item.price,
          id: item?.article_id,
          category: item?.classification_name,
          comment: item?.text || null,
          children: item.products.map((product) => ({
            product: product.product_id,
            quantity: product.quantity,
            price: product.price,
            id: product?.article_id,
            name: product?.name
          }))
        };
        return groupArticle;
      }
      else if (item.type === 'productPackage') {
        let childrenProducts = item.products.map((product) => ({
          product: product.product_id,
          quantity: product.quantity,
          price: product.price,
          id: product?.article_id,
          name: product?.name
        }));
        let childrenProductParts = item.product_parts.map((productPart) => ({
          productPart: productPart.product_part_id,
          quantity: productPart.quantity,
          price: productPart.price,
          id: productPart?.article_id,
          name: productPart?.name
        }));
        const children = childrenProducts.concat(childrenProductParts);
        // Si c'est pour un cocktail, les forfaits n'ont pas de prix directement donc on prend le prix indicatif
        // En mode classique, on utilise le prix directement
        const packageArticle = {
          name: item.name,
          renamedName: item?.renammed || null,
          package: item.package_id,
          quantity: item.quantity,
          // Mode brique : prix porté par l'article (price_per_person), jamais via le modèle caterer par-participant.
          price: briqueMode ? item?.price : ((isCaterer && useClassicPricing === true) ? item?.price : (isCaterer ? null : item?.price)),
          indicativePrice: briqueMode ? null : ((isCaterer && useClassicPricing === true) ? null : (isCaterer ? item?.price : null)),
          id: item?.article_id,
          category: item?.classification_name,
          comment: item?.text || null,
          children: children
        };
        return packageArticle;
      }
      else {
        return {
          product: item.product_id,
          quantity: item.quantity,
          price: item.price,
          id: item?.article_id,
          name: item?.name,
          renamedName: item?.renammed || null,
          category: item?.classification_name,
          catererName: item?.caterer_name || null,
          comment: item?.text || null,
        };
      }
    });

    // Les infos sur les prix (recalcul avec lignes issues des briques B2B fusionnées)
    let priceInfo = {
      totalPriceHt: Math.round(totalHT) || 0,
      totalPriceHtWithoutPromotion: Math.round(totalHTWithoutReduction) || 0,
      useClassicPricing: useClassicPricing !== undefined ? useClassicPricing : null, // Send useClassicPricing
    }

    // Si type traiteur, on ajoute les infos sur les prix par personne
    if (order.prestationType === 'caterer') {
      priceInfo = {
        ...priceInfo,
        catererPackagePiecesPriceHtPerParticipant: briqueMode ? null : (useClassicPricing === true ? null : (Math.round(orderInfo?.orders?.[orderIndex]?.priceInfo?.catererPackagePiecesPriceHtPerParticipant) || null)),
        catererPackageSoftsPriceHtPerParticipant: briqueMode ? null : (useClassicPricing === true ? null : (Math.round(orderInfo?.orders?.[orderIndex]?.priceInfo?.catererPackageSoftsPriceHtPerParticipant) || null)),
        catererPackageAlcoholsPriceHtPerParticipant: briqueMode ? null : (useClassicPricing === true ? null : (Math.round(orderInfo?.orders?.[orderIndex]?.priceInfo?.catererPackageAlcoholsPriceHtPerParticipant) || null)),
        piecesTaxRate: 0.100,
        softsTaxRate: 0.100,
        alcoholsTaxRate: 0.200,
      }
    }

    let deliveryInformation;
    if (orderInfo?.orders?.[orderIndex]?.deliveryInfo?.deliverySpecific === true) {
      deliveryInformation = {
        firstname: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.firstname === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.firstname,
        lastname: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.lastname === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.lastname,
        company: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.company === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.company,
        phone: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.phone === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.phone,
        phoneSecondary: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.phoneSecondary === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.phoneSecondary,
        streetNumber: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.streetNumber === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.streetNumber,
        street: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.street === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.street,
        addressComplement: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.addressComplement === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.addressComplement,
        postalCode: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.postalCode === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.postalCode,
        city: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.city === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.city,
        note: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.textarea === "" ? null : orderInfo?.orders?.[orderIndex]?.deliveryInfo?.textarea,
        deliverySpecific: true,
        deliveryAddressBook: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.deliveryAddressBook?.id || null
      }
    }
    else {
      deliveryInformation = {
        firstname: orderInfo?.deliveryInfo?.firstname === "" ? null : orderInfo?.deliveryInfo?.firstname,
        lastname: orderInfo?.deliveryInfo?.lastname === "" ? null : orderInfo?.deliveryInfo?.lastname,
        company: orderInfo?.deliveryInfo?.company === "" ? null : orderInfo?.deliveryInfo?.company,
        phone: orderInfo?.deliveryInfo?.phone === "" ? null : orderInfo?.deliveryInfo?.phone,
        phoneSecondary: orderInfo?.deliveryInfo?.phoneSecondary === "" ? null : orderInfo?.deliveryInfo?.phoneSecondary,
        streetNumber: orderInfo?.deliveryInfo?.streetNumber === "" ? null : orderInfo?.deliveryInfo?.streetNumber,
        street: orderInfo?.deliveryInfo?.street === "" ? null : orderInfo?.deliveryInfo?.street,
        addressComplement: orderInfo?.deliveryInfo?.addressComplement === "" ? null : orderInfo?.deliveryInfo?.addressComplement,
        postalCode: orderInfo?.deliveryInfo?.postalCode === "" ? null : orderInfo?.deliveryInfo?.postalCode,
        city: orderInfo?.deliveryInfo?.city === "" ? null : orderInfo?.deliveryInfo?.city,
        note: orderInfo?.deliveryInfo?.textarea === "" ? null : orderInfo?.deliveryInfo?.textarea,
        deliverySpecific: false,
        deliveryAddressBook: orderInfo?.deliveryInfo?.deliveryAddressBook?.id || null
      }
    }

    const serviceFlag = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service;
    const serviceNeeded = serviceFlag === true || serviceFlag === 1 || serviceFlag === "1";

    // Fonction pour parser les heures (time strings) ou retourner null si vide
    const parseTimeOrNull = (value) => {
      if (value === "" || value === null || value === undefined) {
        return null;
      }
      // S'assurer que c'est une chaîne valide (format HH:mm)
      const trimmed = String(value).trim();
      return trimmed !== "" ? trimmed : null;
    };

    const roadSheetPayload = {
      serviceNeeded,
      masterCount: parseIntegerOrNull(serviceInfoState?.masterCount),
      nbWaiters: parseIntegerOrNull(serviceInfoState?.waiterCount),
      labDeparture: parseTimeOrNull(serviceInfoState?.labDeparture),
      labArrival: parseTimeOrNull(serviceInfoState?.labArrival),
      labReturn: parseTimeOrNull(serviceInfoState?.labReturn),
    };

    return {
      id: order?.id,
      status: orderInfo?.status,
      // Position dans la commande groupée (drag & drop de la liste des prestations)
      sort: orderIndex,
      prestationType: order.prestationType,
      format: order.format,
      format_tag_id: order.formatTagId || null,
      commentaryForProd: order.commentaryForProd,
      customerSpecialReference: orderInfo.customerSpecialReference,
      customerQuotationCommentary: orderInfo.customerQuotationCommentary,
      customerSpecialCommentary: order?.customerSpecialCommentary || null,
      codeServiceChorus: orderInfo.codeServiceChorus,
      orderFormDate: orderInfo.orderFormDate ? moment(orderInfo.orderFormDate).format(
        "YYYY-MM-DD"
      ) : null,
      disableApproachingMail: orderInfo.disableApproachingMail === true || orderInfo.disableApproachingMail === "1" || orderInfo.disableApproachingMail === 1 ? 1 : 0,
      tender: orderInfo?.tender?.id ? orderInfo.tender.id.toString() : null,
      packagingType: order.packagingType || null,
      accountManager: orderInfo.account_manager || null,
      isBriqueMode: order.briqMode ? 1 : 0,
      briqueDevisJson: order.briqMode && order.briqueDevis?.length
        ? JSON.stringify(order.briqueDevis)
        : null,
      cart: {
        customer: orderInfo?.customer?.id ? orderInfo.customer.id.toString() : null,
        company: orderInfo?.company?.id ? orderInfo.company.id.toString() : null,
        articles: articles,
        deliveryInfo: {
          deliveryDate: deliveryDate,
          recoveryDate: recoveryDate,
          recoveryStartAt: recoveryStartAt,
          recoveryEndAt: recoveryEndAt,
          arrivedAtStart: deliveryArrivedAtStart,
          arrivedAtEnd: deliveryArrivedAtEnd,
          startAt: deliveryStartAt,
          endAt: deliveryEndAt,
          deliveryInformation:  orderInfo?.orders?.[orderIndex]?.deliveryInfo?.deliveryInformation ? orderInfo.orders[orderIndex].deliveryInfo.deliveryInformation : null,
          recoverInformation: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.recoverInformation ? orderInfo.orders[orderIndex].deliveryInfo.recoverInformation : null,
          firstname: deliveryInformation?.firstname,
          lastname: deliveryInformation?.lastname,
          company: deliveryInformation?.company,
          phone: deliveryInformation?.phone,
          phoneSecondary: deliveryInformation?.phoneSecondary,
          streetNumber: deliveryInformation?.streetNumber,
          street: deliveryInformation?.street,
          addressComplement: deliveryInformation?.addressComplement,
          postalCode: deliveryInformation?.postalCode,
          city: deliveryInformation?.city,
          note: deliveryInformation?.note,
          recovery: "",
          service: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service === true || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service === "1" || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service === 1 ? 1 : 0,
          blanket: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.blanket === true || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.blanket === "1" || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.blanket === 1 ? 1 : 0,
          type: 'v2',
          person: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.person ? orderInfo.orders[orderIndex].deliveryInfo.person.toString() : null,
          deliveryCostHt: order.deliveryCost,
          deliverySpecific: deliveryInformation?.deliverySpecific === true ? 1 : 0,
          deliveryAddressBook: deliveryInformation?.deliveryAddressBook,
        },
        priceInfo: priceInfo,
        billingInfo: {
          firstname: orderInfo?.billingInfo?.firstname === "" ? null : orderInfo?.billingInfo?.firstname,
          lastname: orderInfo?.billingInfo?.lastname === "" ? null : orderInfo?.billingInfo?.lastname,
          company: orderInfo?.billingInfo?.company === "" ? null : orderInfo?.billingInfo?.company,
          phone: orderInfo?.billingInfo?.phone === "" ? null : orderInfo?.billingInfo?.phone,
          address: orderInfo?.billingInfo?.fullAddress === "" ? null : orderInfo?.billingInfo?.fullAddress,
          company: orderInfo?.billingInfo?.company === "" ? null : orderInfo?.billingInfo?.company,
          moreInformation: orderInfo?.billingInfo.addressComplement === "" ? null : orderInfo?.billingInfo?.addressComplement,
          service: orderInfo?.billingInfo?.service === "" ? null : orderInfo?.billingInfo?.service,
        },
        customerInfo: {
          firstname: orderInfo?.billingInfo?.firstname === "" ? null : orderInfo?.billingInfo?.firstname,
          lastname: orderInfo?.billingInfo?.lastname === "" ? null : orderInfo?.billingInfo?.lastname,
          company: orderInfo?.billingInfo?.company === "" ? null : orderInfo?.billingInfo?.company,
          streetNumber: orderInfo?.billingInfo?.streetNumber === "" ? null : orderInfo?.billingInfo?.streetNumber,
          street: orderInfo?.billingInfo?.street === "" ? null : orderInfo?.billingInfo?.street,
          postalCode: orderInfo?.billingInfo?.postalCode === "" ? null : orderInfo?.billingInfo?.postalCode,
          city: orderInfo?.billingInfo?.city === "" ? null : orderInfo?.billingInfo?.city,
          addressComplement: orderInfo?.billingInfo?.addressComplement === "" ? null : orderInfo?.billingInfo?.addressComplement,
          phone: orderInfo?.billingInfo?.phone === "" ? null : orderInfo?.billingInfo?.phone,
        },
        promotions: (() => {
          // Liste des réductions (nouveau modèle multiple) avec repli sur l'ancien
          // champ unique `order.reduction`.
          const reductionsList = Array.isArray(order?.reductions)
            ? order.reductions
            : (order?.reduction && (order.reduction?.direct || order.reduction?.proportional) ? [order.reduction] : []);
          const valides = reductionsList.filter((r) => r && (r.direct || r.proportional));
          if (!valides.length) {
            return null;
          }
          // Base HT (items + livraison) pour convertir un % en montant HT.
          const baseHt = orderStateToTotal(order)?.totalHTWithoutReduction || 0;
          return valides.map((r) => {
            const tva = (r.tva !== undefined && r.tva !== null && r.tva !== "") ? Number(r.tva) : 10;
            const ht = r.direct
              ? (r.direct === "" ? 0 : Number(r.direct))
              : Math.round((baseHt * (Number(r.proportional) || 0)) / 100);
            return {
              name: r.label || null,
              type: r.type || null,
              amount: (r.direct === "" || !r.direct) ? null : Number(r.direct),
              percent: (r.proportional === "" || !r.proportional) ? null : Number(r.proportional),
              ht: ht,
              // Taux de TVA stocké en fraction (5,5 % -> 0.055). Pas de `ttc` :
              // l'entité Promotion le recalcule depuis `ht` + `tax`.
              tax: Math.round((tva / 100) * 1000) / 1000,
            };
          });
        })(),
      },
      roadSheet: roadSheetPayload
    }
  });
};

export const breakfastOrderStoreToApi = (orderIndex, orderInfo) => {
  const deliveryDate = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.deliveryDate
  ? moment(orderInfo.orders[orderIndex].deliveryInfo.deliveryDate, "YYYY/MM/DD").format("DD/MM/YYYY")
  : null;
  const billingAddress = `${orderInfo?.billingInfo?.streetNumber || ''} ${orderInfo?.billingInfo?.street || ''}, ${orderInfo?.billingInfo?.postalCode || ''}, ${orderInfo?.billingInfo.city || ''}`;
  const deliveryAddress = `${orderInfo?.deliveryInfo?.streetNumber || ''} ${orderInfo?.deliveryInfo?.street || ''}, ${orderInfo?.deliveryInfo?.postalCode || ''}, ${orderInfo?.deliveryInfo?.city || ''}`;

  const deliveryStartAt = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.startAt ? orderInfo.orders[orderIndex].deliveryInfo.startAt : null;
  const deliveryEndAt = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.endAt ? orderInfo.orders[orderIndex].deliveryInfo.endAt : null;
  const deliveryArrivedAtStart = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.arrivedAtStart ? orderInfo.orders[orderIndex].deliveryInfo.arrivedAtStart : null;
  const deliveryArrivedAtEnd = orderInfo?.orders?.[orderIndex]?.deliveryInfo?.arrivedAtEnd ? orderInfo.orders[orderIndex].deliveryInfo.arrivedAtEnd : null;
  return {
    customer: orderInfo?.customer?.id ? orderInfo.customer.id.toString() : null,
    company: orderInfo?.company?.id ? orderInfo.company.id.toString() : null,
    delivery_date: deliveryDate,
    tender: orderInfo?.tender?.id ? orderInfo.tender.id.toString() : null,
    confirmTender: orderInfo?.confirmTender ? orderInfo.confirmTender : null,
    customerSpecialCommentary: orderInfo?.customerSpecialCommentary || null,
    disable_approaching_mail: orderInfo.disableApproachingMail === true || orderInfo.disableApproachingMail === "1" || orderInfo.disableApproachingMail === 1 ? 1 : 0,
    account_manager: orderInfo.accountManager || null,
    orderFormDate: orderInfo?.orderFormDate ? moment(
      orderInfo.orderFormDate,
      "YYYY/MM/DD"
    ).format("YYYY-MM-DD") : null,
    customer_special_reference: orderInfo.customerSpecialReference === "" ? null : orderInfo?.customerSpecialReference,
    codeServiceChorus: orderInfo.codeServiceChorus === "" ? null : orderInfo?.codeServiceChorus,
    customer_quotation_commentary:
      orderInfo?.customerQuotationCommentary || null,
    commentaryForProd: orderInfo.orders[orderIndex].commentaryForProd,
    deliveryAddress: deliveryAddress === "" ? null : deliveryAddress,
    billingAddress: billingAddress === "" ? null : billingAddress,
    firstname: orderInfo?.billingInfo?.firstname === "" ? null : orderInfo?.billingInfo?.firstname,
    lastname: orderInfo?.billingInfo?.lastname === "" ? null : orderInfo?.billingInfo?.lastname,
    billingCompany: orderInfo?.company?.name === "" ? null : orderInfo?.company?.name,
    phone: orderInfo?.billingInfo?.phone === "" ? null : orderInfo?.billingInfo?.phone,
    address: billingAddress === "" ? null : billingAddress,
    street_number: orderInfo?.billingInfo?.streetNumber === "" ? null : orderInfo?.billingInfo?.streetNumber,
    street: orderInfo?.billingInfo?.street === "" ? null : orderInfo?.billingInfo?.street,
    address_complement: orderInfo?.billingInfo.addressComplement === "" ? null : orderInfo?.billingInfo?.addressComplement,
    postal_code: orderInfo?.billingInfo?.postalCode === "" ? null : orderInfo?.billingInfo?.postalCode,
    city: orderInfo?.billingInfo?.city === "" ? null : orderInfo?.billingInfo?.city,
    service: orderInfo?.billingInfo?.service === "" ? null : orderInfo?.billingInfo?.service,
    format: orderInfo.orders[orderIndex].format,
    participants_number: orderInfo?.orders[orderIndex]?.deliveryInfo?.person ? orderInfo.orders[orderIndex].deliveryInfo.person.toString() : null,
    status: orderInfo?.orders[orderIndex]?.status || '101',
    delivery: {
      arrived_at_start: deliveryArrivedAtStart,
      arrived_at_end: deliveryArrivedAtEnd,
      start_at: deliveryStartAt,
      end_at: deliveryEndAt,
      delivery_information: orderInfo.deliveryInfo.deliveryInformation === "" ? null : orderInfo?.deliveryInfo?.deliveryInformation,
      recover_information: orderInfo.deliveryInfo.recoverInformation === "" ? null : orderInfo?.deliveryInfo?.recoverInformation,
      firstname: orderInfo?.deliveryInfo?.firstname === "" ? null : orderInfo?.deliveryInfo?.firstname,
      lastname: orderInfo?.deliveryInfo?.lastname === "" ? null : orderInfo?.deliveryInfo?.lastname,
      company: orderInfo?.deliveryInfo?.company === "" ? null : orderInfo?.deliveryInfo?.company,
      phone: orderInfo?.deliveryInfo?.phone === "" ? null : orderInfo?.deliveryInfo?.phone,
      phone_secondary: orderInfo?.customer?.delivery_phone_secondary === "" ? null : orderInfo?.customer?.delivery_phone_secondary,
      street_number: orderInfo?.deliveryInfo?.streetNumber === "" ? null : orderInfo?.deliveryInfo?.streetNumber,
      street: orderInfo?.deliveryInfo?.street === "" ? null : orderInfo?.deliveryInfo?.street,
      address_complement: orderInfo?.deliveryInfo?.addressComplement === "" ? null : orderInfo?.deliveryInfo?.addressComplement,
      postal_code: orderInfo?.deliveryInfo?.postalCode === "" ? null : orderInfo?.deliveryInfo?.postalCode,
      city: orderInfo?.deliveryInfo?.city === "" ? null : orderInfo?.deliveryInfo?.city,
      note: orderInfo?.deliveryInfo?.textarea === "" ? null : orderInfo?.deliveryInfo?.textarea,
      recovery: "",
      service: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service === true || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service === "1" || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.service === 1 ? 1 : 0,
      blanket: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.blanket === true || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.blanket === "1" || orderInfo?.orders?.[orderIndex]?.deliveryInfo?.blanket === 1 ? 1 : 0,
      deliveryAddressBook: orderInfo?.orders?.[orderIndex]?.deliveryInfo?.deliveryAddressBook,
    },
    formulas: orderInfo.orders[orderIndex].items
    .filter((item) => item.type === 'formula')
    .map((selectedFormula) => {
      const formulaComponents = selectedFormula.formula_elements.map(
        (element) => {
          return {
            id: element.breakfast_product.id.toString(),
            quantity: element.quantity_per_person.toString(),
            type: "menu",
          };
        }
      );
  
      return {
        name: selectedFormula.name,
        quantity: selectedFormula.quantity,
        price: selectedFormula.price,
        components: formulaComponents,
      };
    }),
    products: orderInfo.orders[orderIndex].items
    .filter((item) => item.type === 'product')
    .map((selectedProduct) => {
      return {
        quantity: selectedProduct.quantity,
        name: selectedProduct.name,
        price: selectedProduct.price,
        id: selectedProduct.id,
      };
    }),
    deliveryCost: orderInfo.orders[orderIndex].deliveryCost,
    reductions: Array.isArray(orderInfo?.orders[orderIndex]?.reductions)
      ? orderInfo.orders[orderIndex].reductions
      : [],
    reduction: orderInfo?.orders[orderIndex]?.reduction ? {
      label: orderInfo?.orders[orderIndex]?.reduction?.label || "",
      direct: orderInfo?.orders[orderIndex]?.reduction?.direct || 0,
      proportional: orderInfo?.orders[orderIndex]?.reduction?.proportional || 0,
    } : null,
  };
};

export const breakfastOrderApiToStore = (order) => {

  let orderState = {
    deliveryInfo: {
      deliveryDate: moment(order.delivery_date, "DD/MM/YYYY").format(
        "YYYY/MM/DD"
      ),
      arrivedAtStart: order.delivery.arrived_at_start,
      arrivedAtEnd: order.delivery.arrived_at_end,
      startAt: order.delivery.start_at,
      endAt: order.delivery.end_at,
      deliveryInformation: order.delivery.delivery_information,
      recoverInformation: order.delivery.recover_information,
      firstname: order.delivery.firstname,
      lastname: order.delivery.lastname,
      company: order.delivery.company,
      phone: order.delivery.phone,
      phoneSecondary: order.delivery.phone_secondary,
      streetNumber: order.delivery.street_number,
      street: order.delivery.street,
      addressComplement: order.delivery.address_complement,
      postalCode: order.delivery.postal_code,
      city: order.delivery.city,
      codeChorus: order.codeServiceChorus,
      customerQuotationCommentary: order.customer_quotation_commentary,
    },
    billingInfo: {
      firstname: order.firstname,
      lastname: order.lastname,
      company: order.billingCompany,
      streetNumber: order.street_number,
      street: order.street,
      postalCode: order.postal_code,
      city: order.city,
      addressComplement: order.address_complement,
      phone: order.phone,
    },
    customer: order.customer ? {
      id: order.customer.id,
      firstname: order.customer.firstname,
      lastname: order.customer.lastname
    } : null,
    company: order.company ? {
      id: order.company.id,
      name: order.company.name,
    } : null,
    tender: order.tender ? {
      id: order.tender.id,
      name: order.tender.name,
    } : null,
    currentOrder: {},
    currentOrderIndex: null,
    customerSpecialCommentary: order.customer_quotation_commentary,
    customerSpecialReference: order.customer_special_reference,
    codeServiceChorus: order.code_service_chorus,
    orderFormDate: order.order_form_date ? moment(order.order_form_date).format(
      "YYYY-MM-DD"
    ) : null,
    disableApproachingMail: order.disable_approaching_mail === true || order.disable_approaching_mail === "1" || order.disable_approaching_mail=== 1,
    confirmTender: order.id ? true : false,
    accountManager: order.account_manager || null,
    orders: [
      {
        id: order.id,
        version: order.version,
        status: order.status,
        format: order.format,
        prestationType: 'breakfast',
        commentaryForProd: order.commentary_for_prod,
        priceInfo: {
          totalHT: order.total_price
        },
        reduction: order.components
        .reduce((acc, component) => {
          if (component.type === 'Promotion') {
            return {
              label: component.name,
              direct: -1 * (acc.direct + component.price),
              proportional: 0,
            }
          }
          return acc;
        }, {
          label: "",
          direct: 0,
          proportional: 0,
        }),
        deliveryCost: order.components
        .reduce((acc, component) => {
          if (component.type === 'Delivery') {
            return acc + component.price;
          }
          return acc;
        }, 0),
        deliveryInfo: {
          startAt: order.delivery.start_at ? moment.parseZone(order.delivery.start_at).format("HH:mm") : "",
          endAt: order.delivery.end_at ? moment.parseZone(order.delivery.end_at).format("HH:mm") : "",
          deliveryDate: moment(order.delivery_date).format(
            "YYYY-MM-DD"
          ),
          person: order.participants_number,
          arrivedAtStart: order.delivery.arrived_at_start ? moment.parseZone(order.delivery.arrived_at_start).format("HH:mm") : "",
          arrivedAtEnd: order.delivery.arrived_at_end ? moment.parseZone(order.delivery.arrived_at_end).format("HH:mm") : "",
          service: order.delivery.service === true || order.delivery.service === "1" || order.delivery.service === 1,
          blanket: order.delivery.blanket === true || order.delivery.blanket === "1" || order.delivery.blanket === 1,
        },
        items: order.components
        .reduce((acc, component) => {

          if (component.type === 'menu') {
            // Trouver l'élément existant dans le tableau accumulé
            let existingItem = acc.find(item => item.name === component.formula_name && item.price === component.formula_price && item.quantity === component.formula_quantity);
          
            // Si l'élément existe déjà, ajoutez simplement le nouveau composant à formula_elements
            if (existingItem) {
              existingItem.formula_elements.push({
                breakfast_product: {
                  id: component.product_id,
                },
                quantity_per_person: component.quantity,
              });
            } else {
              // Sinon, créez un nouvel élément et ajoutez-le à l'accumulateur
              acc.push({
                name: component.formula_name,
                price: component.formula_price,
                quantity: component.formula_quantity,
                type: 'formula',
                formula_elements: [{
                  breakfast_product: {
                    id: component.product_id,
                  },
                  quantity_per_person: component.quantity,
                }],
              });
            }
          }
          else {
            // Ne prend que les composants de type supplement et special. Les autres (Delivery, Promotion sont traités autres part)
            if (component.type === 'supplement' || component.type === 'special') {
              acc.push({
                name: component.name,
                price: component.price,
                quantity: component.quantity,
                type: 'product',
                id: component.product_id,
              });
            }
          }
        
          return acc;
        }, []),
        info: order.info
      },
    ],
  };

  const totals = orderStateToTotal(orderState.orders[0]);
  orderState.orders[0].totalHT = totals.totalHT;

  return orderState;
}

export const groupedOrderApiToStore = (groupedOrder) => {
  // Ordre canonique (sort ASC, id ASC), identique au GET backend. La réponse du PUT
  // renvoie les prestations créées par le save (duplications) en fin de collection :
  // sans ce tri, le state est réordonné après sauvegarde et order_index/currentOrderIndex
  // pointent silencieusement sur une autre prestation.
  groupedOrder = {
    ...groupedOrder,
    orders: [...groupedOrder.orders].sort(
      (a, b) => ((a.sort ?? 0) - (b.sort ?? 0)) || ((a.id ?? 0) - (b.id ?? 0))
    ),
  };
  // Identifiants de briques (_devisId) déjà pris par une prestation précédente.
  // Une prestation dupliquée avant le correctif de DUPLICATE_ORDER porte les mêmes
  // _devisId que l'original : l'écran des briques les confondait et recopiait le
  // contenu de l'une dans l'autre. On régénère les doublons au chargement.
  const seenBriqueDevisIds = new Set();
  let firstOrder = groupedOrder.orders[0];
  const savInProgress = groupedOrder.sav_in_progress === true || groupedOrder.sav_in_progress === "1" || groupedOrder.savInProgress === true || groupedOrder.savInProgress === "1";

  // Pour les infos de livraison, prends les infos de la première commande non spécifique
  let deliveryInfo = {
    firstname: firstOrder.delivery.firstname,
    lastname: firstOrder.delivery.lastname,
    company: firstOrder.delivery.company,
    phone: firstOrder.delivery.phone,
    phoneSecondary: firstOrder.delivery.phone_secondary,
    streetNumber: firstOrder.delivery.street_number,
    street: firstOrder.delivery.street,
    addressComplement: firstOrder.delivery.address_complement,
    postalCode: firstOrder.delivery.postal_code,
    city: firstOrder.delivery.city,
    codeChorus: firstOrder.codeServiceChorus,
    deliveryAddressBook: firstOrder.delivery.delivery_address_book || null,
  };
  groupedOrder.orders.forEach((order) => {
    if (order.delivery.delivery_specific !== true) {
      deliveryInfo = {
        firstname: order.delivery.firstname,
        lastname: order.delivery.lastname,
        company: order.delivery.company,
        phone: order.delivery.phone,
        phoneSecondary: order.delivery.phone_secondary,
        streetNumber: order.delivery.street_number,
        street: order.delivery.street,
        addressComplement: order.delivery.address_complement,
        postalCode: order.delivery.postal_code,
        city: order.delivery.city,
        codeChorus: order.codeServiceChorus,
        deliveryAddressBook: order.delivery.delivery_address_book,
      };
      return;
    }
  });
  let orderState = {
    status: groupedOrder.status,
    deliveryInfo: deliveryInfo,
    billingInfo: {
      firstname: firstOrder.billing_info.firstname,
      lastname: firstOrder.billing_info.lastname,
      company: firstOrder.billing_info.company,
      streetNumber: firstOrder.billing_info.street_number,
      street: firstOrder.billing_info.street,
      postalCode: firstOrder.billing_info.postal_code,
      city: firstOrder.billing_info.city,
      addressComplement: firstOrder.billing_info.more_information,
      phone: firstOrder.billing_info.phone,
      fullAddress: firstOrder.billing_info.address,
      service: firstOrder.billing_info.service,
    },
    customer: firstOrder.customer ? {
      id: firstOrder.customer.id,
      firstname: firstOrder.customer.contact_firstname,
      lastname: firstOrder.customer.contact_lastname
    } : null,
    company: firstOrder.company ? {
      id: firstOrder.company.id,
      name: firstOrder.company.name,
    } : null,
    tender: firstOrder.tender ? {
      id: firstOrder.tender.id,
      name: firstOrder.tender.name,
    } : null,
    currentOrder: {},
    customerSpecialReference: firstOrder?.customer_special_reference || null,
    codeServiceChorus: firstOrder?.code_service_chorus || null,
    customerQuotationCommentary: firstOrder?.customer_quotation_commentary || null,
    orderFormDate: firstOrder.order_form_date ? moment(firstOrder.order_form_date).format(
      "YYYY-MM-DD"
    ) : null,
    disableApproachingMail: firstOrder.disable_approaching_mail === true || firstOrder.disable_approaching_mail === "1" || firstOrder.disable_approaching_mail=== 1,
    confirmTender: true,
    accountManager: firstOrder.account_manager || null,
    savInProgress,
    orders: groupedOrder.orders.map((order) => {
      const specificDelivery = order?.delivery?.delivery_specific ? {
        firstname: order.delivery.firstname,
        lastname: order.delivery.lastname,
        company: order.delivery.company,
        phone: order.delivery.phone,
        phoneSecondary: order.delivery.phone_secondary,
        streetNumber: order.delivery.street_number,
        street: order.delivery.street,
        addressComplement: order.delivery.address_complement,
        postalCode: order.delivery.postal_code,
        city: order.delivery.city,
        note: order.delivery.note,
        deliveryAddressBook: order.delivery.delivery_address_book,
      } : {};
      const roadSheet = order.road_sheet || order.roadSheet || {};

      const useClassicPricing = order?.price_info?.use_classic_pricing !== undefined ? order?.price_info?.use_classic_pricing : null;

      // Liste des articles dont au moins une FK (product / productPart / group / package)
      // pointait vers une entité offre archivée côté backend. Cf. `Article` (API field
      // `has_archived_reference`) + `Archivable\HasArchivedReferenceTrait`.
      // Utilisé pour :
      //  - styler en rouge les lignes du cart en mode classique ;
      //  - styler en rouge les slots du devis brique (cross-join par FK) ;
      //  - désactiver le bouton "Sauvegarder" tant qu'il en reste.
      const archivedArticles = (order.articles || [])
        .filter((article) => article.has_archived_reference === true)
        .map((article) => ({
          id: article.id,
          name: article.renamed_name || article.name,
          fields: Array.isArray(article.archived_reference_fields) ? article.archived_reference_fields : [],
          product_id: article.product_id || null,
          product_part_id: article.product_part_id || null,
          group_id: article.group_id || null,
          package_id: article.package_id || null,
        }));

      /*
       * 10/2026 - La composition réellement enregistrée sur le devis, groupe par groupe.
       *
       * L'éditeur affichait la composition du **catalogue du jour** pour un groupe, alors que le
       * document du client, le devis de production et les fiches lisent tous les enfants stockés
       * sur la ligne. L'éditeur était le seul à montrer autre chose : un produit renommé depuis la
       * vente y apparaissait sous son nouveau nom, et l'écart restait invisible.
       *
       * On la garde au niveau de la commande et non dans les items du devis brique : ces items
       * sont sérialisés tels quels dans `brique_devis_json`, et y copier la composition créerait
       * une seconde vérité à maintenir.
       */
      const storedGroupCompositions = (order.articles || [])
        .filter((article) => article.item_type === 'group' && article.group_id)
        .map((article) => ({
          group_id: article.group_id,
          products: (article.children || []).map((child) => ({
            product_id: child.product_id,
            name: child.renamed_name || child.name,
            quantity: child.quantity,
          })),
        }));

      // Helper : indique si un slot brique pointe vers un article archivé,
      // en matchant par la FK offre (product_id / package_id / group_id / product_part_id).
      const briqueItemHasArchivedRef = (briqueItem) => {
        if (!briqueItem || archivedArticles.length === 0) return false;
        return archivedArticles.some((a) => {
          if (briqueItem.type === 'forfait')  return briqueItem.package_id      && a.package_id      === briqueItem.package_id;
          if (briqueItem.type === 'group')    return briqueItem.product_group_id && a.group_id       === briqueItem.product_group_id;
          if (briqueItem.type === 'product')  return briqueItem.product_id      && a.product_id      === briqueItem.product_id;
          if (briqueItem.type === 'part')     return briqueItem.product_part_id && a.product_part_id === briqueItem.product_part_id;
          return false;
        });
      };

      return {
        id: order.id,
        version: order.version,
        prestationType: order.prestation_type,
        status: order.status,
        googleCalendarEventId: order?.google_calendar_event_id || order?.googleCalendarEventId || null,
        format: order.format,
        formatTagId: order.format_tag_id || order.formatTagId || null,
        commentaryForProd: order?.commentary_for_prod || null,
        customerSpecialCommentary: order?.customer_special_commentary || null,
        packagingType: order.packaging_type || null,
        priceInfo: {
          totalHT: order?.price_info?.total_price_ht || 0,
          totalTTC: order?.price_info?.total_price_ttc || order?.total_price_ttc || 0,
          totalHTWithoutReduction: order?.price_info?.total_price_ht_without_promotion || 0,
          totalTTCWithoutReduction: order?.price_info?.total_price_ttc_without_promotion || 0,
          catererPackagePiecesPriceHtPerParticipant: order?.price_info?.caterer_package_pieces_price_ht_per_participant || null,
          catererPackageSoftsPriceHtPerParticipant: order?.price_info?.caterer_package_softs_price_ht_per_participant || null,
          catererPackageAlcoholsPriceHtPerParticipant: order?.price_info?.caterer_package_alcohols_price_ht_per_participant || null,
          useClassicPricing: useClassicPricing
        },
        // Plusieurs réductions typées (Commercial/SAV) + TVA par réduction.
        reductions: Array.isArray(order?.promotions) ? order.promotions.map((reduction) => ({
          label: reduction?.name || "",
          type: reduction?.type || null,
          direct: reduction?.amount || null,
          // Le contrat API est asymétrique, et c'est voulu par le formulaire :
          // on ENVOIE des points (10 = 10 %), que Symfony PercentType (mode
          // `fractional` par défaut) divise par 100 avant de persister ; on
          // RELIT la valeur brute de l'entité, donc une fraction (0.1 = 10 %).
          // Vérifié en base : pro_cart_promotion.percent vaut 0.1 / 0.15 / 1.
          // Le x100 à la relecture est donc correct, ne pas le retirer.
          proportional: reduction?.percent ? reduction.percent * 100 : null,
          tva: (reduction?.tax !== undefined && reduction?.tax !== null) ? Math.round(Number(reduction.tax) * 1000) / 10 : 10,
        })) : [],
        // Rétro-compat : ancien champ unique alimenté par la 1re réduction.
        reduction: (Array.isArray(order?.promotions) && order.promotions.length) ? {
          label: order.promotions[0]?.name || null,
          type: order.promotions[0]?.type || null,
          direct: order.promotions[0]?.amount || null,
          proportional: order.promotions[0]?.percent ? order.promotions[0].percent * 100 : null,
          tva: (order.promotions[0]?.tax !== undefined && order.promotions[0]?.tax !== null) ? Math.round(Number(order.promotions[0].tax) * 1000) / 10 : 10,
        } : {
          label: null,
          direct: null,
          proportional: null,
        },
        deliveryCost: order?.delivery?.delivery_cost_ht || 0,
        deliveryInfo: {
          startAt: order.delivery.start_at ? moment.parseZone(order.delivery.start_at).format("HH:mm") : "",
          endAt: order.delivery.end_at ? moment.parseZone(order.delivery.end_at).format("HH:mm") : "",
          deliveryDate: moment(order.delivery.delivery_date).format(
            "YYYY-MM-DD"
          ),
          recoveryDate: order.delivery.recovery_date ? moment(order.delivery.recovery_date).format("YYYY-MM-DD") : "",
          recoveryStartAt: order.delivery.recovery_start_at ? moment.parseZone(order.delivery.recovery_start_at).format("HH:mm") : "",
          recoveryEndAt: order.delivery.recovery_end_at ? moment.parseZone(order.delivery.recovery_end_at).format("HH:mm") : "",
          person: order.participants_number,
          arrivedAtStart: order.delivery.arrived_at_start ? moment.parseZone(order.delivery.arrived_at_start).format("HH:mm") : "",
          arrivedAtEnd: order.delivery.arrived_at_end ? moment.parseZone(order.delivery.arrived_at_end).format("HH:mm") : "",
          service: order.delivery.service === true || order.delivery.service === "1" || order.delivery.service === 1,
          blanket: order.delivery.blanket === true || order.delivery.blanket === "1" || order.delivery.blanket === 1,
          deliveryInformation: order?.delivery?.delivery_information || null,
          recoverInformation: order?.delivery?.recover_information || null,
          deliverySpecific: order?.delivery?.delivery_specific || false,
          ...specificDelivery
        },
        serviceInfo: {
          masterCount: roadSheet?.master_count ?? roadSheet?.masterCount ?? 1,
          waiterCount: roadSheet?.nb_waiters ?? roadSheet?.waiterCount ?? 0,
          labDeparture: roadSheet?.lab_departure ?? roadSheet?.labDeparture ?? "",
          labArrival: roadSheet?.lab_arrival ?? roadSheet?.labArrival ?? "",
          labReturn: roadSheet?.lab_return ?? roadSheet?.labReturn ?? ""
        },
        briqMode: order.is_brique_mode === true || order.is_brique_mode === 1 || order.is_brique_mode === "1",
        archivedArticles,
        storedGroupCompositions,
        briqueDevis: (() => {
          try {
            const raw = order.brique_devis_json;
            if (!raw || raw === 'null') return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];
            // Cross-join : pour chaque slot brique, marquer s'il pointe vers un
            // article du cart dont une FK pointe vers une entité offre archivée.
            return parsed.map((brique) => ({
              ...brique,
              _devisId: (() => {
                let devisId = brique._devisId;
                if (!devisId || seenBriqueDevisIds.has(devisId)) {
                  devisId = `devis_brique_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
                }
                seenBriqueDevisIds.add(devisId);
                return devisId;
              })(),
              items: Array.isArray(brique.items)
                ? brique.items.map((it) => ({
                    ...it,
                    archived_reference: briqueItemHasArchivedRef(it),
                  }))
                : brique.items,
            }));
          } catch (e) {
            return [];
          }
        })(),
        // En mode brique, briqueDevis est la source de vérité pour les articles.
        // On vide items pour éviter la "copie fantôme" qui persiste en mode classique
        // quand on supprime un article côté brique.
        items: (order.is_brique_mode === true || order.is_brique_mode === 1 || order.is_brique_mode === "1")
          ? []
          : order.articles
        .reduce((acc, article) => {

          if (article.item_type === 'group') {
            acc.push({
              name: article.name,
              renammed: article?.renamed_name || null,
              price: article.price,
              quantity: article.quantity,
              type: 'productGroup',
              group_id: article.group_id,
              article_id: article.id,
              classification_name: article.category,
              caterer_price_group: article.caterer_price_group,
              has_archived_reference: article.has_archived_reference === true,
              archived_reference_fields: Array.isArray(article.archived_reference_fields) ? article.archived_reference_fields : [],
              products: article.children.map((child) => ({
                id: child.id,
                name: child.name,
                quantity: child.quantity,
                product_id: child.product_id,
                article_id: child.id,
                sweet: child.sweet,
                salty: child.salted,
                alcohol: child.alcohol,
                soft: child.soft
              })),
            });
          }
          else if (article.item_type === 'package') {
            acc.push({
              name: article.name,
              renammed: article?.renamed_name || null,
              price: order.prestation_type === 'caterer' && useClassicPricing !== true ? article.indicative_price : article.price,
              quantity: article.quantity,
              type: 'productPackage',
              package_id: article.package_id,
              article_id: article.id,
              classification_name: article.category,
              caterer_price_group: article.caterer_price_group,
              has_archived_reference: article.has_archived_reference === true,
              archived_reference_fields: Array.isArray(article.archived_reference_fields) ? article.archived_reference_fields : [],
              products: article.children.map((child) => {
                if (child.item_type === 'product') {
                  return {
                    id: child.id,
                    name: child.name,
                    quantity: child.quantity,
                    product_id: child.product_id,
                    article_id: child.id,
                    sweet: child.sweet,
                    salty: child.salted,
                    alcohol: child.alcohol,
                    soft: child.soft,
                  }
                }
                return null;
              })
              .filter((child) => child !== null),
              product_parts: article.children.map((child) => {
                if (child.item_type === 'productPart') {
                  return {
                    id: child.id,
                    name: child.name,
                    quantity: child.quantity,
                    product_part_id: child.product_part_id,
                    article_id: child.id,
                    sweet: child.sweet,
                    salty: child.salted,
                    alcohol: child.alcohol,
                    soft: child.soft,
                  }
                }
                return null;
              })
              .filter((child) => child !== null),
            });
          }
          else {
            acc.push({
              name: article.name,
              renammed: article?.renamed_name || null,
              price: article.price,
              quantity: article.quantity,
              type: article.item_type,
              product_id: article.product_id || null,
              product_part_id: article.product_part_id || null,
              article_id: article.id,
              classification_name: article.category,
              caterer_name: article?.caterer_name || null,
              sweet: article.sweet,
              salty: article.salted,
              alcohol: article.alcohol,
              soft: article.soft,
              has_archived_reference: article.has_archived_reference === true,
              archived_reference_fields: Array.isArray(article.archived_reference_fields) ? article.archived_reference_fields : [],
            });
          }
        
          return acc;
        }, []),
        info: order.info
      }
    }),
  };
  return orderState;
}
